import type { AppProps } from 'next/app'
import { IntlProvider } from 'react-intl'
import { useSelector } from 'react-redux'
import { datadogRum } from '@datadog/browser-rum'
import * as AppHealth from '@vinted/apphealth-web'

import { BreakpointProvider } from 'components/Breakpoint'
import ErrorBoundary from 'components/ErrorBoundary'
import BrazeProvider from 'containers/BrazeProvider'
import NextRequestProvider from 'containers/RequestProvider/NextRequestProvider'
import TrackingProvider from 'containers/TrackingProvider'
import InpostAbTestGlobalTracking from 'components/InpostAbTestGlobalTracking'

import { serverSide } from 'libs/utils/environment'
import { getLocale, getMessages } from 'state/intl/selectors'

import BottomScripts from '../components/BottomScripts'
import ConsentBannerScript from '../components/ConsentBannerScript'
import GoogleTagManager from '../components/GoogleTagManager'
import AppHead from '../components/Head/AppHead'
import TrackScreen from '../components/TrackScreen'
import { AppProps as CustomAppProps } from '../libs/server-utils/ssr'
import { wrapper } from '../state/store'

import 'startup/intlPolyfills'
import './app.scss'

if (!serverSide && process.env.NODE_ENV === 'production') {
  // TODO: also check if run in local env to avoid triggering production logs locally
  const version = process.env.NEXT_PUBLIC_RELEASE_VERSION || 'unknown'
  const env = window.location.host.includes('sandbox') ? 'sandbox' : 'production'
  const ddProxyUrl = 'https://rum-collector.svc.vinted.com/'

  AppHealth.setup('core-next', {
    context: {
      version,
      env,
    },
  })
  window.apphealth = AppHealth
  datadogRum.init({
    applicationId: 'b90722a0-1c29-48b4-af9c-34fffb86338b',
    clientToken: 'pub9fd180c090593448102410bf135f044e',
    site: 'datadoghq.eu',
    proxy: ddProxyUrl,
    sessionSampleRate: 10,
    sessionReplaySampleRate: 100,
    trackUserInteractions: true,
    trackFrustrations: true,
    trackResources: true,
    service: 'core-next',
    env,
    version,
    allowedTracingUrls: [window.location.origin],
  })

  datadogRum.setGlobalContextProperty('imageTag', process.env.NEXT_PUBLIC_IMG_TAG)
}

type Props = {
  _app: CustomAppProps
}

const App = ({ Component, pageProps, router }: AppProps<Partial<Props>>) => {
  const { _app: appProps, ...componentProps } = pageProps

  const locale = useSelector(getLocale)
  const messages = useSelector(getMessages)

  if (router.pathname === '/500' || router.pathname === '/404' || router.pathname === '/_error') {
    return (
      <div className="next-page">
        <AppHead />
        <Component {...componentProps} />
      </div>
    )
  }

  if (!appProps) throw new Error('Page is rendered without layout wrapper')

  return (
    <>
      <AppHead url={appProps.url} />
      <IntlProvider locale={locale} messages={messages}>
        <ErrorBoundary FallbackComponent={ErrorBoundary.AppError}>
          <TrackingProvider>
            <NextRequestProvider locationUrl={appProps.url}>
              <BrazeProvider>
                <TrackScreen>
                  <BreakpointProvider viewportSize={appProps.viewportSize} device={appProps.device}>
                    <div className="next-page">
                      <Component {...componentProps} />
                    </div>
                    <GoogleTagManager />
                    <ConsentBannerScript />
                    <BottomScripts />
                    <InpostAbTestGlobalTracking />
                  </BreakpointProvider>
                </TrackScreen>
              </BrazeProvider>
            </NextRequestProvider>
          </TrackingProvider>
        </ErrorBoundary>
      </IntlProvider>
    </>
  )
}

const AppWithStore = wrapper.withRedux(App)

export default AppWithStore
